import PropTypes from "prop-types";
import React, {useEffect, useMemo, useRef} from "react";
import Tr from 'translations/Translations';
import {AuctionType} from "constants/AuctionType";
import {AuctionStatus} from "constants/AuctionStatus";
import MultiButton, {createButton} from "components/popup/components/MultiButton";
import {AuctionRule} from "constants/AuctionRule";
import moment from "libs/moment";
import AuctionTypeList from "./AuctionTypeList";

//to widzimy tylko w statusie OFFER_COLLECTION i OFFER_DECRYPTION
function AuctionTypePart(
    {
        demandId,
        editable,
        working,
        auctionType,
        auctions,
        buttonWorks,
        setCreatePopupOpened,
        deleteAuction,
        editAuction,
        publishAuction,
        unpublishAuction,
        cancelAuction,
        pauseAuction,
        acceptAuction,
        resumeAuction,
        auctionDetails,
        serverBrowserTimeDiff,
        prodAuctionStatus
    }) {

    const timeout = useRef(0);

    useEffect(() => {
        const auction = auctions.length > 0 ? auctions[auctions.length - 1] : null;
        if (auction != null) {
            if (auction.status === AuctionStatus.PENDING) {
                const secondsLeftToStart = moment(auction.startDate).diff(moment(), 's') - serverBrowserTimeDiff.current;
                timeout.current = setTimeout(auctionDetails, (secondsLeftToStart + 2) * 1000, auction)
            } else if (auction.status === AuctionStatus.OPEN || auction.status === AuctionStatus.PAUSED) {
                if (auction.rule === AuctionRule.JAPANESE) {
                    const secondsFromStart = moment().diff(moment(auction.startDate), 's') - serverBrowserTimeDiff.current;
                    const secondsToWindowEnd = Math.ceil(secondsFromStart / auction.decisionTime) + 2;
                    timeout.current = setTimeout(auctionDetails, secondsToWindowEnd * 1000, auction)
                } else {
                    const secondsLeftToEnd = moment(auction.endDate).diff(moment(), 's') - serverBrowserTimeDiff.current;
                    timeout.current = setTimeout(auctionDetails, (secondsLeftToEnd + 2) * 1000, auction)
                }
            }
        }
        return () => {
            if (timeout.current)
                clearTimeout(timeout.current)
        }
    }, [auctions, auctionDetails, serverBrowserTimeDiff])


    const getButtons = () => {
        const btns = [];
        const disabled = !editable || working || buttonWorks.anyWorks;
        if (auctions.length > 0) {
            const auction = auctions[auctions.length - 1];
            switch (auction.status) {
                case AuctionStatus.INCOMPLETE:
                    if (auctionType === AuctionType.PROD) {
                        btns.push(createButton(Tr('cancel'), () => cancelAuction(auction), 'outlined', disabled, undefined, buttonWorks.cancel))
                    } else {
                        btns.push(createButton(Tr('delete'), () => deleteAuction(auction), 'outlined', disabled, undefined, buttonWorks.remove))
                    }
                    btns.push(createButton(Tr('edit'), () => editAuction(auction), 'outlined', disabled))
                    btns.push(createButton(Tr('publish'), () => publishAuction(auction), 'contained', disabled, undefined, buttonWorks.publish))
                    break;
                case AuctionStatus.PENDING:
                    btns.push(createButton(Tr('unpublish'), () => unpublishAuction(auction), 'outlined', disabled, undefined, buttonWorks.unpublish))
                    break;
                case AuctionStatus.OPEN:
                    if (auction.rule !== AuctionRule.JAPANESE) {
                        btns.push(createButton(Tr('pause'), () => pauseAuction(auction), 'outlined', disabled, undefined, buttonWorks.pause))
                    }
                    break;
                case AuctionStatus.PAUSED:
                    btns.push(createButton(Tr('cancel'), () => cancelAuction(auction), 'outlined', disabled, undefined, buttonWorks.cancel))
                    btns.push(createButton(Tr('edit'), () => editAuction(auction), 'outlined', disabled))
                    btns.push(createButton(Tr('resume'), () => resumeAuction(auction), 'contained', disabled, undefined, buttonWorks.resume))
                    break;
                case AuctionStatus.FINISHED:
                    if (auctionType === AuctionType.PROD) {
                        btns.push(createButton(Tr('cancel'), () => cancelAuction(auction), 'outlined', disabled, undefined, buttonWorks.cancel))
                        btns.push(createButton(Tr('auction_accept'), () => acceptAuction(auction), 'contained', disabled, undefined, buttonWorks.accept))
                    } else {
                        if (!prodAuctionStatus || prodAuctionStatus === AuctionStatus.CANCELLED) {
                            btns.push(createButton(Tr('auction_create_button'), () => setCreatePopupOpened(auctionType), 'contained', disabled))
                        }
                    }
                    break;
                case AuctionStatus.CANCELLED:
                    if (auctionType === AuctionType.PROD || (!prodAuctionStatus || prodAuctionStatus === AuctionStatus.CANCELLED)) {
                        btns.push(createButton(Tr('auction_create_button'), () => setCreatePopupOpened(auctionType), 'contained', disabled));
                    }
                    break;
                default:
            }
        } else {
            if (auctionType === AuctionType.PROD || (!prodAuctionStatus || prodAuctionStatus === AuctionStatus.CANCELLED)) {
                btns.push(createButton(Tr('auction_create_button'), () => setCreatePopupOpened(auctionType), 'contained', disabled))
            }
        }
        return btns;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const buttons = useMemo(getButtons, [editable, working, auctionType, auctions, buttonWorks]);

    return (
        <div>
            <AuctionTypeList auctions={auctions} auctionType={auctionType}/>
            <MultiButton justify='flex-start' buttons={buttons}/>
        </div>)
}

AuctionTypePart.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    editable: PropTypes.bool.isRequired,
    working: PropTypes.bool.isRequired,
    auctionType: PropTypes.string.isRequired,
    auctions: PropTypes.array.isRequired,
    buttonWorks: PropTypes.object.isRequired,
    setCreatePopupOpened: PropTypes.func.isRequired,
    deleteAuction: PropTypes.func.isRequired,
    editAuction: PropTypes.func.isRequired,
    publishAuction: PropTypes.func.isRequired,
    unpublishAuction: PropTypes.func.isRequired,
    cancelAuction: PropTypes.func.isRequired,
    pauseAuction: PropTypes.func.isRequired,
    resumeAuction: PropTypes.func.isRequired,
    auctionDetails: PropTypes.func.isRequired,
    acceptAuction: function (props, propName, componentName) {
        if ((props['auctionType'] === AuctionType.PROD && (props[propName] === undefined || typeof (props[propName]) !== 'function'))) {
            return new Error('Please provide a acceptAuction function!');
        }
    },
    serverBrowserTimeDiff: PropTypes.object.isRequired,
    prodAuctionStatus: function (props, propName, componentName) {
        if ((props['auctionType'] === AuctionType.TEST && (props[propName] !== undefined && typeof (props[propName]) !== 'string'))) {
            return new Error('Please provide a valid prodAuctionStatus string');
        }
    },

}

export default AuctionTypePart;
